/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/no-cycle */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BusinessTime } from '@src/components/AdminHome/BusinessTime/businessTime.type';
import { DepartmentType, ErrorPayload } from '@src/types';

type RequestStatus = 'idle' | 'pending' | 'success' | 'failure' | 'fulfill';

type BusinessTimeState = {
  request: {
    status: RequestStatus;
    error?: ErrorPayload;
    action?: 'fetch' | 'update' | 'create' | 'delete';
  };
  data: BusinessTime | null;
};

const initialState: BusinessTimeState = {
  request: {
    status: 'idle',
    error: undefined,
  },
  data: null,
};

const businessTimeSlice = createSlice({
  name: 'businessTimeSlice',
  initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchBusinessTime: (
      state,
      _: PayloadAction<{
        botId: number;
        departmentId?: number;
        departmentType: DepartmentType;
      }>
    ) => {
      state.request.status = 'pending';
      state.request.error = undefined;
      state.request.action = 'fetch';
    },
    fetchBusinessTimeSuccess(state, action: PayloadAction<BusinessTime>) {
      state.request.status = 'success';
      state.data = action.payload;
    },
    fetchBusinessTimeFailure(state, action: PayloadAction<ErrorPayload>) {
      state.request.status = 'failure';
      state.request.error = action.payload;
    },

    copyBusinessTimeFromBot(state, _: PayloadAction<{ botId: number }>) {
      state.request.status = 'pending';
      state.request.error = undefined;
    },
    copyBusinessTimeFromBotSuccess(
      state,
      {
        payload: { timesPerHoliDay, timesPerWeekDay },
      }: PayloadAction<
        Pick<BusinessTime, 'timesPerHoliDay' | 'timesPerWeekDay'>
      >
    ) {
      state.request.status = 'success';
      state.request.action = 'fetch';
      state.data = {
        ...state.data,
        timesPerHoliDay,
        timesPerWeekDay,
      };
    },
    copyBusinessTimeFromBotFailure(state, action: PayloadAction<ErrorPayload>) {
      state.request.status = 'failure';
      state.request.error = action.payload;
    },
    updateBusinessTime(
      state,
      _: PayloadAction<{ botId: number } & BusinessTime>
    ) {
      state.request.status = 'pending';
      state.request.error = undefined;
      state.request.action = 'update';
    },
    updateBusinessTimeSuccess(state, action: PayloadAction<BusinessTime>) {
      state.request.status = 'success';
      state.data = {
        ...state.data,
        ...action.payload,
      };
    },
    updateBusinessTimeFailure(state, action: PayloadAction<ErrorPayload>) {
      state.request.status = 'failure';
      state.request.error = action.payload;
    },
    removeBusinessTime(state, _: PayloadAction<{ botId: number }>) {
      state.request.status = 'pending';
      state.request.error = undefined;
      state.request.action = 'delete';
    },
    removeBusinessTimeSuccess(state) {
      state.data = {
        ...state.data,
        timesPerHoliDay: [],
        timesPerWeekDay: [],
      };
      state.request.status = 'success';
    },
    removeBusinessTimeFailure(state, action: PayloadAction<ErrorPayload>) {
      state.request.status = 'failure';
      state.request.error = action.payload;
    },

    updateMerchantAvailability(
      state,
      _: PayloadAction<{ botId: number } & BusinessTime>
    ) {
      state.request.status = 'pending';
      state.request.error = undefined;
    },
    updateMerchantAvailabilitySuccess(
      state,
      action: PayloadAction<BusinessTime>
    ) {
      state.request.status = 'success';
      state.data = action.payload;
    },
    updateMerchantAvailabilityFailure(
      state,
      action: PayloadAction<ErrorPayload>
    ) {
      state.request.status = 'failure';
      state.request.error = action.payload;
    },
    resetRequestState(state) {
      state.request = initialState.request;
    },
    resetState() {
      return initialState;
    },
  },
});

export default businessTimeSlice.reducer;

export const {
  fetchBusinessTime,
  fetchBusinessTimeSuccess,
  fetchBusinessTimeFailure,
  copyBusinessTimeFromBot,
  copyBusinessTimeFromBotSuccess,
  copyBusinessTimeFromBotFailure,
  updateBusinessTime,
  updateBusinessTimeSuccess,
  updateBusinessTimeFailure,
  updateMerchantAvailability,
  updateMerchantAvailabilitySuccess,
  updateMerchantAvailabilityFailure,
  removeBusinessTime,
  removeBusinessTimeSuccess,
  removeBusinessTimeFailure,
  resetRequestState,
  resetState,
} = businessTimeSlice.actions;

export const businessTimeActions = businessTimeSlice.actions;
